/**
 * Derive canonical (English) faction identity from a strategy for HUD colors, icons, and backgrounds.
 * Must use faction / faction_name_english — not faction_name, which is UI-localized.
 */

const COMBINED_FACTIONS = {
  'The Conspiracy': ['League of Shadows', 'Asrah'],
  'The Corrupted Core': ['Exolot Dominion', 'Asrah'],
  'The Covenant': ['Ra\'s Children', 'League of Shadows'],
  'The Divine Syndicate': ['Ra\'s Children', 'Exolot Dominion'],
  'The Forsaken Pact': ['Remnants of Humanity', 'Asrah'],
  'The Hollow': ['Ra\'s Children', 'Asrah'],
  'The Iron Alliance': ['Remnants of Humanity', 'Exolot Dominion'],
  'The Last Crusade': ['Ra\'s Children', 'Remnants of Humanity'],
  'The Shadow Consortium': ['League of Shadows', 'Exolot Dominion'],
  'The Silent Accord': ['Remnants of Humanity', 'League of Shadows'],
};

const BASE_FACTION_NAMES = [
  'Asrah',
  'Exolot Dominion',
  'League of Shadows',
  'Ra\'s Children',
  'Remnants of Humanity',
];

const COMBINED_FACTION_NAMES = Object.keys(COMBINED_FACTIONS);

const ALL_CANONICAL_FACTION_NAMES = [
  ...BASE_FACTION_NAMES,
  ...COMBINED_FACTION_NAMES,
  'The Sentients',
];

export function getOrderEnglishFactionName(order) {
  if (!order) {
    throw new Error('getOrderEnglishFactionName: order is required');
  }
  if (order.faction_name_english) {
    return order.faction_name_english;
  }
  if (order.faction) {
    return order.faction;
  }
  // getStrategyWithOrders stores canonical English in faction_name only (no separate faction field).
  if (order.faction_name && ALL_CANONICAL_FACTION_NAMES.includes(order.faction_name)) {
    return order.faction_name;
  }
  throw new Error(
    `getOrderEnglishFactionName: order ${order.id ?? '?'} missing English faction fields`
  );
}

export function getBaseFactionsFromStrategy(strategy) {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return [];
  }

  const uniqueFactions = [
    ...new Set(strategy.orders.map((order) => getOrderEnglishFactionName(order))),
  ];

  const baseFactions = uniqueFactions
    .filter((f) => f && f.trim() !== '')
    .filter((f) => !COMBINED_FACTION_NAMES.includes(f))
    .filter((f) => BASE_FACTION_NAMES.includes(f) || f === 'The Sentients');

  return baseFactions.filter((f) => f !== 'The Sentients');
}

function isPureSentientsStrategy(strategy) {
  if (!strategy?.orders?.length) {
    return false;
  }
  return strategy.orders.every((order) => getOrderEnglishFactionName(order) === 'The Sentients');
}

export function calculateFactionFromStrategy(strategy) {
  if (!strategy || !strategy.orders || strategy.orders.length === 0) {
    return null;
  }

  const strategyEnglishFaction =
    strategy.faction_name_english ||
    strategy.faction ||
    (strategy.faction_name && ALL_CANONICAL_FACTION_NAMES.includes(strategy.faction_name)
      ? strategy.faction_name
      : null);
  if (strategyEnglishFaction) {
    return strategyEnglishFaction;
  }

  const baseFactionsOnly = getBaseFactionsFromStrategy(strategy);

  if (baseFactionsOnly.length === 0) {
    return isPureSentientsStrategy(strategy) ? 'The Sentients' : 'Unknown';
  }
  if (baseFactionsOnly.length === 1) {
    return baseFactionsOnly[0];
  }

  const sortedBaseFactions = [...baseFactionsOnly].sort();

  for (const [combinedFactionName, requiredFactions] of Object.entries(COMBINED_FACTIONS)) {
    const sortedRequired = [...requiredFactions].sort();
    const baseSet = new Set(sortedBaseFactions);
    const requiredSet = new Set(sortedRequired);

    if (
      baseSet.size === requiredSet.size &&
      sortedBaseFactions.every((f) => requiredSet.has(f)) &&
      sortedRequired.every((f) => baseSet.has(f))
    ) {
      return combinedFactionName;
    }
  }

  return baseFactionsOnly[0];
}
